import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, first, map, mergeMap } from 'rxjs/operators';
import {
  EBookshelfConcurrencyResponse,
  isLtiParams,
  LogoutResponse,
  LtiParams,
  PlayerApiClientError,
  TTSChunk,
  TTSChunkCollection,
  TTSRawTextRequest,
  TTSRawTextTranslationResponse,
  TTSRequest,
} from '@mhe/reader/models';
import * as readerConfigQuery from '../state/configuration/configuration.selectors';
import { IReaderClient } from '../../models/reader.client.model';
import { TtsApiClient } from '../tts-api/tts-api.client.service';
import { FeatureTogglesService } from '@mhe/reader/features/feature-toggles.service';

@Injectable({ providedIn: 'root' })
export class PlayerApiClient implements IReaderClient {
  private readonly baseUrl$: Observable<string | undefined>;

  constructor(
    store: Store,
    private readonly http: HttpClient,
    private readonly ttsApiClient: TtsApiClient,
    private readonly featureTogglesService: FeatureTogglesService,
  ) {
    // TODO support dark Player API URL
    this.baseUrl$ = store.pipe(
      select(readerConfigQuery.getPlayerApi),
      filter((s: string | undefined) => typeof s === 'string' && s.length > 0),
      first(),
    );
  }

  fetchLTIParams(): Observable<LtiParams> {
    return this.baseUrl$.pipe(
      mergeMap((baseUrl) =>
        this.http.get<LtiParams>(`${baseUrl}/lti`, {
          withCredentials: true,
        }),
      ),
      first(),
      map((rsp) => {
        if (!isLtiParams(rsp)) {
          throw new PlayerApiClientError(
            'get',
            '/lti',
            'Response is not a valid set of LTI parameters',
          );
        }
        return rsp;
      }),
    );
  }

  checkEbookshelfConcurrency(): Observable<EBookshelfConcurrencyResponse> {
    return this.baseUrl$.pipe(
      mergeMap((baseUrl) => {
        return this.http.get<EBookshelfConcurrencyResponse>(
          `${baseUrl}/concurrency`,
          {
            withCredentials: true,
          },
        );
      }),
    );
  }

  /**
   * Player API
   *   content: XHTML
   *   language: en
   *   gender: MALE
   *
   * TTS API
   *   text: XHTML
   *   language: en-US
   *   voice: en-US-AvaMultilingualNeural
   *   gender: Female,
   *   destination: context_id
   */

  /**
   * Sends content to be translated for text-to-speech. In
   * the response, the first chunk mp3 url is provided and
   * the metadata.requestCacheKey is used to poll an endpoint
   * for completion of the remaining chunks.
   */
  createTextToSpeechJob(params: TTSRequest, tocItemId?: string): Observable<TTSChunk | null> {
    return this.featureTogglesService.isTtsApiEnabled$.pipe(
      first(),
      mergeMap((isTtsApiEnabled) => {
        if (isTtsApiEnabled) {
          return this.ttsApiClient.createTextToSpeechJob(params, tocItemId);
        }

        return this.baseUrl$.pipe(
          mergeMap((playerApiBaseUrl) => {
            const url = `${playerApiBaseUrl}/text-to-speech`;
            const formData = new FormData();
            Object.keys(params).forEach((key) => formData.append(key, params[key]));

            return this.http.post<TTSChunk>(url, formData, {
              withCredentials: true,
            });
          }),
        );
      }),
    );
  }

  /**
   * Player API
   *   requestCacheKey: tts-en-MALE-3f1cb27d27a007638163078daef0cffa
   *
   * TTS API
   *   destination: sn_55
   */

  /**
   * Obtains status on an existing text-to-speech job. The resonse contains
   * multiple ordered chunks
   */
  fetchTextToSpeechJob(
    requestCacheKey: string,
  ): Observable<TTSChunkCollection> {
    return this.featureTogglesService.isTtsApiEnabled$.pipe(
      first(),
      mergeMap((isTtsApiEnabled) => {
        if (isTtsApiEnabled) {
          return this.ttsApiClient.fetchTextToSpeechJob(requestCacheKey);
        }

        return this.baseUrl$.pipe(
          mergeMap((playerApiBaseUrl) => {
            const url = `${playerApiBaseUrl}/text-to-speech/status`;
            return this.http.get<TTSChunkCollection>(url, {
              params: {
                requestCacheKey,
              },
              withCredentials: true,
            });
          }),
        );
      }),
    );
  }

  /**
   * Player API
   *   text: plain text
   *   language: en
   *   gender: MALE
   *
   * TTS API
   *   text: plain text
   *   language: en-US
   *   voice: en-US-AvaMultilingualNeural
   *   gender: Female
   *   destination: context_id
   */

  /**
   * Translates raw text to speech audio URL.
   */
  translateRawTextToSpeech(
    params: TTSRawTextRequest,
  ): Observable<TTSRawTextTranslationResponse> {
    return this.featureTogglesService.isTtsApiEnabled$.pipe(
      first(),
      mergeMap((isTtsApiEnabled) => {
        if (isTtsApiEnabled) {
          return this.ttsApiClient.translateRawTextToSpeech(params);
        }

        return this.baseUrl$.pipe(
          mergeMap((playerApiBaseUrl) => {
            const url = `${playerApiBaseUrl}/readspeaker`;
            const formData = new FormData();
            Object.keys(params).forEach((key) => formData.append(key, params[key]));
            return this.http.post<TTSRawTextTranslationResponse>(url, formData, {
              withCredentials: true,
            });
          }),
        );
      }),
    );
  }

  logout(): Observable<LogoutResponse> {
    return this.baseUrl$.pipe(
      mergeMap((baseUrl) => {
        return this.http.get<LogoutResponse>(`${baseUrl}/logout`, {
          withCredentials: true,
        });
      }),
    );
  }
}
